import { Phone } from "lucide-react";
import {
  useCanAccess,
  useGetIdentity,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
  useUpdate,
} from "ra-core";
import { ShowBase } from "ra-core";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { Lead, LeadStatus } from "../types";
import { ClaimLeadButton } from "./ClaimLeadButton";
import { LeadSendMessage } from "./LeadSendMessage";
import { LeadStatusBadge } from "./LeadStatusBadge";
import { LeadTimeline } from "./LeadTimeline";
import { ResponseTime } from "./ResponseTime";
import { formatDuration, LEAD_STATUSES } from "./leadUtils";

const StatusSelect = ({ lead }: { lead: Lead }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isPending }] = useUpdate();

  return (
    <Select
      value={lead.status}
      disabled={isPending}
      onValueChange={(status) =>
        update(
          "leads",
          { id: lead.id, data: { status }, previousData: lead },
          {
            onSuccess: () => {
              notify(translate("resources.leads.notification.status_changed"), {
                type: "success",
              });
              refresh();
            },
            onError: (error) =>
              notify(error instanceof Error ? error.message : "Fehler", {
                type: "error",
              }),
          },
        )
      }
    >
      <SelectTrigger className="w-48">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {LEAD_STATUSES.map((status: LeadStatus) => (
          <SelectItem key={status} value={status}>
            {translate(`resources.leads.status.${status}`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const Detail = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col">
      <span className="text-xs text-muted-foreground">{translate(label)}</span>
      <span className="text-sm">{value ?? "–"}</span>
    </div>
  );
};

const LeadShowContent = () => {
  const lead = useRecordContext<Lead>();
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  // Status ändern dürfen nur Zuständige und Admins (RLS "Eigene Leads bearbeiten")
  const { canAccess: isAdmin } = useCanAccess({
    resource: "sales",
    action: "list",
  });
  if (!lead) return null;

  const canEditStatus =
    lead.sales_id != null && (lead.sales_id === identity?.id || isAdmin);
  const name =
    [lead.first_name, lead.last_name].filter(Boolean).join(" ") ||
    lead.phone ||
    `#${lead.id}`;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-2">
      <div className="md:col-span-2 flex flex-col gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CardTitle>{name}</CardTitle>
              <LeadStatusBadge status={lead.status} />
            </div>
            <div className="flex items-center gap-2">
              {lead.phone ? (
                <Button variant="outline" size="sm" asChild>
                  <a href={`tel:${lead.phone.replace(/\s/g, "")}`}>
                    <Phone className="h-4 w-4" />
                    {translate("resources.leads.action.call")}
                  </a>
                </Button>
              ) : null}
              <ClaimLeadButton lead={lead} />
              {canEditStatus ? <StatusSelect lead={lead} /> : null}
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Detail label="resources.leads.fields.phone" value={lead.phone} />
            <Detail label="resources.leads.fields.email" value={lead.email} />
            <Detail
              label="resources.leads.fields.source"
              value={`${translate(`resources.leads.source.${lead.source}`)}${
                lead.campaign ? ` · ${lead.campaign}` : ""
              }`}
            />
            <Detail
              label="resources.leads.fields.first_response_seconds"
              value={<ResponseTime seconds={lead.first_response_seconds} />}
            />
            <Detail
              label="resources.leads.fields.claim_seconds"
              value={formatDuration(lead.claim_seconds)}
            />
            <Detail
              label="resources.leads.fields.sales_id"
              value={lead.sales_name}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{translate("resources.leads.timeline.title")}</CardTitle>
          </CardHeader>
          <CardContent>
            <LeadTimeline leadId={lead.id} />
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader>
            <CardTitle>
              {translate("resources.leads.action.message_title")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <LeadSendMessage lead={lead} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const LeadShow = () => (
  <ShowBase>
    <LeadShowContent />
  </ShowBase>
);
